import React from 'react';
import { JcCommonProps, JcSize, JcUtils } from '../utils/JcUtils';
import { JcColumn, JcRow } from './JcBox';
import { JcButton, JcButtonType } from './button/JcButton';
import { JcLabel } from './JcLabel';
import { JcIcon, JcIconType } from './icon/JcIcon';
import { JcNote } from './JcNote';
import { JcBuiltInIcon } from './icon/JcBuiltInIcons';

export interface JcCollapsibleProps extends JcCommonProps {
  /** Header title, always visible. */
  readonly title: string;
  /** Optional secondary line under the title (e.g. "Optional"). */
  readonly subtitle?: string;
  /** Controlled open state. When provided, drive changes through `onOpenChange`. */
  readonly open: boolean;
  /** Called with the next open state whenever the header is toggled. */
  readonly onOpenChange: (open: boolean) => void;
  readonly disabled?: boolean;
  readonly children: React.ReactNode;
}

/**
 * A disclosure panel: a clickable header that expands or collapses its content
 * with a smooth height transition. While collapsed only the header shows. Works
 * either uncontrolled (`defaultOpen`) or controlled (`open` + `onOpenChange`).
 */
export const JcCollapsible: React.FunctionComponent<JcCollapsibleProps> = (props: JcCollapsibleProps) => {
  const { title, subtitle, open, onOpenChange, disabled, children, ...otherProps } = props;

  const toggle = () => {
    onOpenChange(!open);
  };

  const commonProps = JcUtils.commonProps(otherProps, [
    "jc-collapsible",
    open && "jc-collapsible-open",
  ]);

  const chevronClassName = JcUtils.classNameString([
    "jc-collapsible-chevron",
    open && "jc-collapsible-chevron-open"
  ]);

  return (
    <JcColumn {...commonProps}>
      <JcButton
        className="jc-collapsible-header"
        buttonType={JcButtonType.GHOST}
        onClick={toggle}
        ariaExpanded={open}
        disabled={disabled}
      >
        <JcRow className="jc-collapsible-header-row" spacing alignCenter>
          <JcColumn flexGrow className="jc-collapsible-header-text">
            <JcLabel className="jc-collapsible-header-label" label={title} />
            {subtitle && <JcNote text={subtitle} />}
          </JcColumn>
          <JcIcon
            className={chevronClassName}
            type={JcIconType.BUILT_IN}
            icon={JcBuiltInIcon.CHEVRON_DOWN}
            size={JcSize.SMALL}
          />
        </JcRow>
      </JcButton>
      <div className="jc-collapsible-content">
        <JcColumn className="jc-collapsible-content-inner">
          {children}
        </JcColumn>
      </div>
    </JcColumn >
  );
};
